import { AmostrasDeRetorno } from './amostras/Retorno';
import { ComponentShowcase } from './ComponentShowcase';
import { ItemDaDocumentacao } from './ItemDaDocumentacao';
import { CodeBlock } from './CodeBlock';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { PIE_COLORS } from '@/ui/charts/chartTheme';
// O arquivo de verdade, como texto: a referencia abaixo nao tem valor escrito a mao.
import fonteDoChartTheme from '@/ui/charts/chartTheme.tsx?raw';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export function GraficosTab() {
  return (
    <div className="space-y-6">
      {/* Cores das séries */}
      <ComponentShowcase
        id="cores-de-graficos"
        title="Cores de gráficos"
        description="Séries --chart-1 a --chart-6 de tokens.css, no tema em uso — usadas por PieChartCard e BarChartCard via Recharts. Rótulo, legenda e dica vão na cor de texto, nunca na cor da série."
        code={`import { PIE_COLORS, TOOLTIP_STYLE, TOOLTIP_ITEM_STYLE, BarGradientDef } from '@/ui/charts/chartTheme';

// Fatia: hsl(var(--chart-N)), acompanha o tema
<Cell fill={PIE_COLORS[index % PIE_COLORS.length]} />

// Dica: fundo do popover e texto do popover
<Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />

// Degrade da barra
<BarGradientDef />
<Bar fill="url(#chartBarGradient)" />`}
        propsTable={[
          [
            'BarGradientDef: id',
            'string',
            '"chartBarGradient"',
            'Id do degradê da barra, usado em fill="url(#…)"',
          ],
        ]}
      >
        <div className="flex flex-wrap gap-3">
          {PIE_COLORS.map((color, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <div
                className="h-12 w-12 rounded-xl border border-border"
                style={{ backgroundColor: color }}
              />
              <span className="text-[10px] font-mono text-muted-foreground">--chart-{i + 1}</span>
            </div>
          ))}
          {/* Degradê da barra */}
          <div className="flex flex-col items-center gap-1.5">
            <div
              className="h-12 w-12 rounded-xl border border-border"
              style={{
                background: 'linear-gradient(180deg, hsl(var(--chart-1)), hsl(var(--chart-5)))',
              }}
            />
            <span className="text-[10px] font-mono text-muted-foreground">Degradê</span>
          </div>
        </div>
      </ComponentShowcase>

      {/* Alert */}
      <ComponentShowcase
        id="alert"
        title="Alert"
        description="Aviso fixo na página — informação, erro, sucesso e atenção. Como texto colorido, sucesso e atenção usam os tokens -text."
        code={`<Alert>
  <Info className="h-4 w-4" />
  <AlertTitle>Informação</AlertTitle>
  <AlertDescription>Detalhes da mensagem.</AlertDescription>
</Alert>

<Alert variant="destructive">
  <XCircle className="h-4 w-4" />
  <AlertTitle>Erro</AlertTitle>
  <AlertDescription>Algo deu errado.</AlertDescription>
</Alert>`}
        propsTable={[
          ['variant', '"default" | "destructive"', '"default"', 'Tom do aviso'],
          ['className', 'string', '-', 'Sucesso e atenção: borda, fundo e texto -text'],
          ['AlertTitle / AlertDescription', 'ReactNode', '-', 'Título e texto do aviso'],
        ]}
      >
        <div className="flex flex-col gap-3 w-full">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Informação</AlertTitle>
            <AlertDescription>O período de inscrição está aberto.</AlertDescription>
          </Alert>
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Erro ao processar</AlertTitle>
            <AlertDescription>
              Não foi possível gerar a medição. Verifique os dados da ordem de serviço.
            </AlertDescription>
          </Alert>
          <Alert className="border-success/30 bg-success/5 text-success-text [&>svg]:text-success-text">
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>Sucesso</AlertTitle>
            <AlertDescription>Credenciado cadastrado com sucesso no sistema.</AlertDescription>
          </Alert>
          <Alert className="border-warning/30 bg-warning/5 text-warning-text [&>svg]:text-warning-text">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Atenção</AlertTitle>
            <AlertDescription>
              A CNH do condutor vence em 15 dias. Solicite a renovação.
            </AlertDescription>
          </Alert>
        </div>
      </ComponentShowcase>

      {/* Aviso curto */}
      <ComponentShowcase
        id="aviso-curto"
        title="Aviso curto (sonner)"
        description="Aviso temporário pelo sonner, cujo Toaster já está montado no App. Para ação que ainda não grava, mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)."
        code={`import { toast } from 'sonner';

// Padrão
toast('Título', { description: 'Uma frase dizendo o que esperar.' });

// Erro
toast.error('Título', { description: 'Uma frase dizendo o que fazer.' });`}
        propsTable={[
          [
            'toast(titulo, opções)',
            'string, { description? }',
            '-',
            'Aviso padrão, que some sozinho',
          ],
          ['toast.error(titulo, opções)', 'string, { description? }', '-', 'Aviso de erro'],
          [
            'mostrarAviso(aviso)',
            'AVISO_DE_ACAO_INDISPONIVEL | …',
            '-',
            'Ação que ainda não grava (operacao/comum/cadastroIndisponivel.ts)',
          ],
          ['<Toaster />', 'ui/base/sonner', '-', 'Montado uma vez, no App; tela nenhuma o monta'],
        ]}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              toast('Aviso de exemplo', {
                description: 'É assim que um aviso curto aparece na tela.',
              })
            }
          >
            Aviso padrão
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              toast.error('Exemplo de erro', {
                description: 'É assim que um aviso de erro aparece.',
              })
            }
          >
            Aviso de erro
          </Button>
        </div>
      </ComponentShowcase>

      <AmostrasDeRetorno />

      {/* Referência do tema */}
      <ItemDaDocumentacao id="referencia-do-tema">
        <section>
          <h3 className="text-lg font-semibold mb-4">Referência do tema dos gráficos</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Conteúdo de <code>apps/web/src/ui/charts/chartTheme.tsx</code>, lido do próprio arquivo.
            Os valores de cada série nos três temas estão em{' '}
            <code>apps/web/src/ui/estilos/tokens.css</code>.
          </p>
          <CodeBlock code={fonteDoChartTheme} />
        </section>
      </ItemDaDocumentacao>
    </div>
  );
}
